import React, { type RefObject, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { isScrollToElementPossible, scrollToElement } from "../../../utils/scrollUtils";
import * as Styles from "./styles/GradeEngineList";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { trackBrowseEngine } from "../../../utils/tracking";
import type { ModelGradeType } from "../../../../../../common-deprecated/types/CommonTypes";

export type EngineListControlsProps = {
    grade: ModelGradeType;
    listRef: RefObject<HTMLDivElement>;
};

export const EngineListControls = ({ listRef, grade }: EngineListControlsProps): JSX.Element | null => {
    const [initialised, setInitialised] = useState<boolean>(false);
    const [isNavigatePreviousEnabled, setNavigatePrevious] = useState<boolean>(
        isScrollToElementPossible(listRef.current, false),
    );
    const [isNavigateNextEnabled, setNavigateNext] = useState<boolean>(
        isScrollToElementPossible(listRef.current, true),
    );
    const onNavigationClick = (direction: boolean): boolean => {
        if (!listRef.current) return false;

        scrollToElement(listRef.current, direction);
        debouncedScrollhandler();
        trackBrowseEngine(NavStep.ModelEngine, grade.name);
        return true;
    };

    /**
     * Bind scrollListener
     */
    useEffect(() => {
        if (listRef.current) {
            if (!initialised) {
                window.addEventListener("resize", debouncedScrollhandler);
                listRef.current.addEventListener("scroll", debouncedScrollhandler);
                setInitialised(true);
            }
            // Trigger checks again to make sure controls are set correctly
            scrollHandler();
        }
    }, [listRef.current]);

    /**
     * Unbind scrollListener
     * This will only be triggered if the TableControls are the only thing being destroyed or before the table
     * This is very rarely the case but just to cover that case if it ever occurs.
     */
    useEffect(() => {
        return () => {
            if (initialised && listRef.current) {
                listRef.current.removeEventListener("scroll", debouncedScrollhandler);
                window.removeEventListener("resize", debouncedScrollhandler);
            }
        };
    }, []);

    const scrollHandler = (): void => {
        setNavigatePrevious(isScrollToElementPossible(listRef.current, false));
        setNavigateNext(isScrollToElementPossible(listRef.current, true));
    };
    const debouncedScrollhandler = debounce(scrollHandler, 200);

    if (!isNavigatePreviousEnabled && !isNavigateNextEnabled) return null;
    return (
        <Styles.EngineListSliderNavigation aria-hidden="true">
            <Styles.EngineListSliderButton
                type="button"
                onClick={() => onNavigationClick(false)}
                disabled={!isNavigatePreviousEnabled}
            >
                <Icon variant="chevronLeft" />
            </Styles.EngineListSliderButton>
            <Styles.EngineListSliderButton
                type="button"
                onClick={() => onNavigationClick(true)}
                disabled={!isNavigateNextEnabled}
            >
                <Icon variant="chevronRight" />
            </Styles.EngineListSliderButton>
        </Styles.EngineListSliderNavigation>
    );
};
